import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import type { Target } from '@cbhq/identity-shared/types';

import { fingerprintStore, mapTargetToFingerprintProSealedApiKey } from '../store';
import type { AgentVersion } from '../types';
import { PACKAGE_VERSION } from '../version';

import { dynamicImportAgent } from './dynamicImportAgent';

/**
 * Custom error class for SDK loading failures.
 * Carries context about the loading method and whether fallback was triggered.
 */
export class SDKLoadError extends Error {
  public loadMethod: 'dynamic_import' | 'html_preload';

  public fallbackTriggered: boolean;

  public originalError?: Error;

  constructor(
    message: string,
    loadMethod: 'dynamic_import' | 'html_preload',
    fallbackTriggered = false,
    originalError?: Error,
  ) {
    super(message);
    this.name = 'SDKLoadError';
    this.loadMethod = loadMethod;
    this.fallbackTriggered = fallbackTriggered;
    this.originalError = originalError;
    // Preserve original stack trace if available
    if (originalError?.stack) {
      this.stack = originalError.stack;
    }
  }
}

// Global promise to hold the FingerprintJS agent
let fpAgentPromise: Promise<FingerprintAgent> | undefined;

// Prevents duplicate promise chains when multiple components call initAgent()
let htmlPreloadAttempted = false;

// First agent version handed to the package. Re-used by retries and auto-recovery so a later
// `initAgent(target)` call cannot silently switch versions mid-session.
let selectedAgentVersion: AgentVersion | Promise<AgentVersion> | undefined;

export const DEFAULT_AGENT_VERSION: AgentVersion = 'v3';

/**
 * Returns the HTML-preloaded agent for `agentVersion`, if any.
 * `window.fpAgents` holds per-version preloads; the legacy `window.fpAgentPromise` is a v3 preload.
 */
function getHtmlPreload(agentVersion: AgentVersion): Promise<FingerprintAgent | null> | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  if (window.fpAgents) {
    return window.fpAgents[agentVersion];
  }
  return agentVersion === 'v3' ? window.fpAgentPromise : undefined;
}

function getHtmlPreloadLoadTime(agentVersion: AgentVersion): number | undefined {
  if (window.fpAgents) {
    return window.fpAgentLoadTimes?.[agentVersion];
  }
  return window.fpAgentLoadTime;
}

/**
 * Loads the agent for a resolved version:
 * 2. HTML preload for that version available → use with fallback
 * 3. No preload → dynamic import
 */
function loadAgent(
  target: Target,
  agentVersion: AgentVersion,
  sdkLoadStartTime: number,
): Promise<FingerprintAgent> {
  // PATH 2: HTML preload available (one-time check)
  const htmlPreload = getHtmlPreload(agentVersion);
  if (htmlPreload && !htmlPreloadAttempted) {
    htmlPreloadAttempted = true;

    return htmlPreload
      .then((agent) => {
        if (agent === null) {
          // HTML preload failed, fall back to dynamic import
          console.warn('HTML preload failed, falling back to dynamic import');

          const apiKey = mapTargetToFingerprintProSealedApiKey[target];
          const fallbackStartTime = performance.now();
          return dynamicImportAgent(apiKey, agentVersion)
            .then((fallbackAgent) => {
              // Track SDK load time for fallback path
              if (!fingerprintStore.timing.sdkLoadTime) {
                fingerprintStore.timing.sdkLoadTime = performance.now() - fallbackStartTime;
              }

              // Log successful SDK load via fallback after HTML preload returned null
              logEvent('identity_fingerprint_pro_sdk_init_completed', {
                action: ActionType.api,
                componentType: ComponentType.api,
                load_method: 'dynamic_import',
                was_fallback: true,
                perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
                sealed: true,
                agent_version: agentVersion,
                version: PACKAGE_VERSION,
              });

              return fallbackAgent;
            })
            .catch((fallbackError) => {
              // If fallback also fails, throw SDKLoadError with full context
              const errorMessage =
                window.fpAgentError?.message ??
                fallbackError?.message ??
                'HTML preload failed and dynamic import failed';

              throw new SDKLoadError(errorMessage, 'html_preload', true, fallbackError);
            });
        }
        // HTML preload succeeded - track load time
        if (!fingerprintStore.timing.sdkLoadTime) {
          // HTML preload timing: actual SDK load time from HTML page start
          // sdkLoadStartTime fallback: time from initAgent() call (less accurate)
          fingerprintStore.timing.sdkLoadTime =
            getHtmlPreloadLoadTime(agentVersion) ?? performance.now() - sdkLoadStartTime;
        }

        // Log successful SDK load via HTML preload (primary path)
        logEvent('identity_fingerprint_pro_sdk_init_completed', {
          action: ActionType.api,
          componentType: ComponentType.api,
          load_method: 'html_preload',
          was_fallback: false,
          perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
          sealed: true,
          agent_version: agentVersion,
          version: PACKAGE_VERSION,
        });

        return agent;
      })
      .catch((error) => {
        // HTML promise rejected, fall back to dynamic import
        console.warn('HTML preload rejected, falling back to dynamic import', error);

        const apiKey = mapTargetToFingerprintProSealedApiKey[target];
        const fallbackStartTime = performance.now();
        return dynamicImportAgent(apiKey, agentVersion)
          .then((fallbackAgent) => {
            // Track SDK load time for fallback path
            if (!fingerprintStore.timing.sdkLoadTime) {
              fingerprintStore.timing.sdkLoadTime = performance.now() - fallbackStartTime;
            }

            // Log successful SDK load via fallback after HTML promise rejected
            logEvent('identity_fingerprint_pro_sdk_init_completed', {
              action: ActionType.api,
              componentType: ComponentType.api,
              load_method: 'dynamic_import',
              was_fallback: true,
              perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
              sealed: true,
              agent_version: agentVersion,
              version: PACKAGE_VERSION,
            });

            return fallbackAgent;
          })
          .catch((fallbackError) => {
            // If fallback also fails, throw SDKLoadError with full context
            const errorMessage =
              error?.message ??
              fallbackError?.message ??
              'HTML preload rejected and dynamic import failed';

            throw new SDKLoadError(errorMessage, 'html_preload', true, fallbackError ?? error);
          });
      });
  }

  // PATH 3: Dynamic import with retry support
  const apiKey = mapTargetToFingerprintProSealedApiKey[target];
  return dynamicImportAgent(apiKey, agentVersion)
    .then((agent) => {
      // Track SDK load time for dynamic import path
      if (!fingerprintStore.timing.sdkLoadTime) {
        // For dynamic import, we measure from initAgent() call
        fingerprintStore.timing.sdkLoadTime = performance.now() - sdkLoadStartTime;
      }

      // Log successful SDK load via direct dynamic import (no HTML preload)
      logEvent('identity_fingerprint_pro_sdk_init_completed', {
        action: ActionType.api,
        componentType: ComponentType.api,
        load_method: 'dynamic_import',
        was_fallback: false,
        perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
        sealed: true,
        agent_version: agentVersion,
        version: PACKAGE_VERSION,
      });

      return agent;
    })
    .catch((error) => {
      // Clear promise to allow retry on next mount
      fpAgentPromise = undefined;

      // Throw SDKLoadError with context
      throw new SDKLoadError(
        error?.message ?? 'Dynamic import failed',
        'dynamic_import',
        false,
        error,
      );
    });
}

/**
 * Initialize the FingerprintJS Pro agent
 * Three paths:
 * 1. Already initialized → return
 * 2. HTML preload available → use with fallback
 * 3. No preload → dynamic import
 *
 * @param target - The target environment (production, sandbox, development)
 * @param agentVersion - Agent version (or a promise of one) to load. Only the first value passed is
 *   used; defaults to `DEFAULT_AGENT_VERSION`. A promise defers loading until it settles (a rejected
 *   promise falls back to the default).
 */
export function initAgent(
  target: Target = 'production',
  agentVersion?: AgentVersion | Promise<AgentVersion>,
): void {
  // PATH 1: Already initialized
  if (fpAgentPromise) {
    return;
  }

  // Fallback timing - only used if HTML preload timing unavailable
  const sdkLoadStartTime = performance.now();

  // Log that SDK loading has been initiated (fires ONCE per initAgent call)
  logEvent('identity_fingerprint_pro_sdk_init_started', {
    action: ActionType.api,
    componentType: ComponentType.api,
    sealed: true,
    version: PACKAGE_VERSION,
  });

  if (agentVersion !== undefined && selectedAgentVersion === undefined) {
    selectedAgentVersion = agentVersion;
  }
  const versionInput = selectedAgentVersion ?? DEFAULT_AGENT_VERSION;

  if (typeof versionInput === 'string') {
    fpAgentPromise = loadAgent(target, versionInput, sdkLoadStartTime);
    return;
  }

  // Version not decided yet (e.g. waiting on a kill switch): defer loading, not the caller.
  fpAgentPromise = versionInput
    .catch(() => DEFAULT_AGENT_VERSION)
    .then((version) => loadAgent(target, version, sdkLoadStartTime));
}

/**
 * Get the initialized FingerprintJS Pro agent
 * Self-healing: automatically initializes if not already done
 * @returns Promise that resolves to the FingerprintJS agent
 */
export async function getAgent() {
  if (!fpAgentPromise) {
    // Use target from store or default to 'production'
    const actualTarget = fingerprintStore.initConfig?.target;
    const target = actualTarget ?? 'production';

    // Log auto-recovery when getAgent() finds no fpAgentPromise (cold start)
    logEvent('identity_fingerprint_pro_init_recovery', {
      action: ActionType.api,
      componentType: ComponentType.api,
      target: actualTarget ?? 'fallback_production',
      has_init_config: !!fingerprintStore.initConfig,
      version: PACKAGE_VERSION,
    });

    // Auto-initialize the agent
    initAgent(target);

    // After initAgent, fpAgentPromise should always be defined
    // If not, something went wrong
    if (!fpAgentPromise) {
      throw new SDKLoadError(
        'Failed to initialize FingerprintJS Pro agent',
        'dynamic_import',
        false,
      );
    }
  }

  return fpAgentPromise;
}

// Internal function used for testing
// eslint-disable-next-line no-underscore-dangle
export function __INTERNAL__clearAgent() {
  fpAgentPromise = undefined;
  htmlPreloadAttempted = false;
  selectedAgentVersion = undefined;
}
